import { dateKeyFromString, getLocalDateKey } from './reportDates';

/** Last 7 local calendar days including today: [oldest … newest] */
export function buildLast7DayKeys() {
  const keys = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() - i);
    keys.push(getLocalDateKey(d));
  }
  return keys;
}

export function shortWeekdayLabel(dateKey) {
  const parts = String(dateKey).split('-').map(Number);
  if (parts.length < 3) return dateKey;
  const [y, m, day] = parts;
  const dt = new Date(y, m - 1, day);
  return dt.toLocaleDateString(undefined, { weekday: 'short' });
}

function saleValueForMode(sale, mode) {
  if (mode === 'profit') {
    const p = parseFloat(sale.profit);
    if (!Number.isNaN(p)) return p;
    const amt = parseFloat(sale.amount) || 0;
    const c = parseFloat(sale.cost);
    if (!Number.isNaN(c)) return amt - c;
    return 0;
  }
  return parseFloat(sale.amount) || 0;
}

/**
 * @param {Array} salesHistory
 * @param {string[]} dayKeys oldest→newest
 * @param {'profit'|'revenue'} mode
 */
export function weeklySeriesByDay(salesHistory, dayKeys, mode) {
  const set = new Set(dayKeys);
  const totals = Object.fromEntries(dayKeys.map((k) => [k, 0]));
  for (const s of salesHistory || []) {
    const k = dateKeyFromString(s.date);
    if (!set.has(k)) continue;
    totals[k] += saleValueForMode(s, mode);
  }
  return dayKeys.map((dateKey) => ({
    dateKey,
    label: shortWeekdayLabel(dateKey),
    value: Math.round((totals[dateKey] + Number.EPSILON) * 100) / 100,
  }));
}

/**
 * Top products by quantity in the given day keys window.
 */
export function topProductsInWindow(salesHistory, dayKeys, limit = 5) {
  const set = new Set(dayKeys);
  const map = new Map();
  for (const s of salesHistory || []) {
    const k = dateKeyFromString(s.date);
    if (!set.has(k)) continue;
    const name = (s.product && s.product.name) || 'Unknown';
    const id = s.product && s.product.id != null ? String(s.product.id) : name;
    const prev = map.get(id) || { name, qty: 0 };
    prev.qty += Number(s.quantity) || 0;
    prev.name = name;
    map.set(id, prev);
  }
  return [...map.values()].sort((a, b) => b.qty - a.qty).slice(0, limit);
}

/**
 * Approved catalogue products with stock > 0 that sold **zero** POS units in the window.
 * Sorted by on-hand quantity (highest first) — ties up capital if it never moves.
 */
export function slowStockMoversInWindow(salesHistory, products, dayKeys, limit = 5) {
  const set = new Set(dayKeys);
  const soldQty = new Map();
  for (const s of salesHistory || []) {
    const k = dateKeyFromString(s.date);
    if (!set.has(k)) continue;
    const pid = s.product && s.product.id != null ? String(s.product.id) : '';
    if (!pid) continue;
    soldQty.set(pid, (soldQty.get(pid) || 0) + (Number(s.quantity) || 0));
  }

  const out = [];
  for (const p of products || []) {
    const id = p.id != null ? String(p.id) : '';
    if (!id) continue;
    const stock = Number(p.quantity) || 0;
    if (stock <= 0) continue;
    const sold = soldQty.get(id) || 0;
    if (sold === 0) {
      out.push({ id, name: (p.name && String(p.name).trim()) || 'Unknown', stockQty: stock });
    }
  }
  out.sort((a, b) => b.stockQty - a.stockQty || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return out.slice(0, limit);
}

/**
 * Wakala cashbook lines (channels) with the most transactions in the window.
 * Excludes `other-services` rows — same boundary as commission-based wakala totals.
 */
export function topWakalaChannelsByTxnCount(transactions, dayKeys, limit = 5) {
  const set = new Set(dayKeys);
  const map = new Map();
  for (const t of transactions || []) {
    const k = txDateKey(t);
    if (!set.has(k)) continue;
    if (t.service === 'other-services') continue;
    const channel = t.channel || 'other';
    const prev = map.get(channel) || { channel, qty: 0 };
    prev.qty += 1;
    map.set(channel, prev);
  }
  return [...map.values()].sort((a, b) => b.qty - a.qty).slice(0, limit);
}

export function txDateKey(t) {
  return t.dateKey || dateKeyFromString(t.dateTime) || '';
}

/** POS product sales: profit for one calendar day (TZS). */
/** POS totals for a pre-filtered sales list (matches Daily / Weekly report tables). */
export function posTotalsFromSales(sales) {
  let qty = 0;
  let amount = 0;
  let profit = 0;
  for (const s of sales || []) {
    qty += Number(s.quantity) || 0;
    amount += parseFloat(s.amount) || 0;
    let p = parseFloat(s.profit);
    if (Number.isNaN(p)) {
      const a = parseFloat(s.amount);
      const c = parseFloat(s.cost);
      p = !Number.isNaN(a) && !Number.isNaN(c) ? a - c : 0;
    }
    profit += p;
  }
  return {
    qty,
    amount: Math.round((amount + Number.EPSILON) * 100) / 100,
    profit: Math.round((profit + Number.EPSILON) * 100) / 100,
    count: (sales || []).length,
  };
}

export function posProfitForDay(salesHistory, dayKey) {
  let sum = 0;
  for (const s of salesHistory || []) {
    if (dateKeyFromString(s.date) !== dayKey) continue;
    const p = parseFloat(s.profit);
    if (!Number.isNaN(p)) sum += p;
    else {
      const amt = parseFloat(s.amount) || 0;
      const c = parseFloat(s.cost);
      if (!Number.isNaN(c)) sum += amt - c;
    }
  }
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/**
 * Printing: sum of paid amounts for jobs whose **created** date is that day
 * (collections are grouped by job open date — see UI hint).
 */
export function printingPaidForDayByJobCreated(printJobs, dayKey) {
  let sum = 0;
  for (const j of printJobs || []) {
    if (dateKeyFromString(j.createdAt) !== dayKey) continue;
    sum += Number(j.paidAmount) || 0;
  }
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/** Wakala: commission booked on cashbook lines (excludes “other services” rows). */
export function wakalaCommissionForDay(transactions, dayKey) {
  let sum = 0;
  for (const t of transactions || []) {
    if (txDateKey(t) !== dayKey) continue;
    if (t.service === 'other-services') continue;
    sum += Number(t.commission) || 0;
  }
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/** Other services: legacy cashbook rows (before separate other-service-sales table). */
export function otherServicesCashInForDay(transactions, dayKey) {
  let sum = 0;
  for (const t of transactions || []) {
    if (txDateKey(t) !== dayKey) continue;
    if (t.service !== 'other-services') continue;
    if (t.direction !== 'in' && t.direction !== 'out') continue;
    sum += Number(t.amount) || 0;
  }
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/** Other services module: `amountTzs` per `dateKey` (from /other-service-sales). */
export function otherServicesRevenueForDay(otherServiceSales, dayKey) {
  let sum = 0;
  for (const s of otherServiceSales || []) {
    if (String(s.dateKey) !== dayKey) continue;
    sum += Number(s.amountTzs) || 0;
  }
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/**
 * @param {Array} [otherServiceSales] optional rows from Other Service Sales API
 */
export function businessDayBreakdown(salesHistory, printJobs, transactions, dayKey, otherServiceSales = []) {
  const pos = posProfitForDay(salesHistory, dayKey);
  const printing = printingPaidForDayByJobCreated(printJobs, dayKey);
  const wakala = wakalaCommissionForDay(transactions, dayKey);
  const other =
    otherServicesRevenueForDay(otherServiceSales, dayKey) + otherServicesCashInForDay(transactions, dayKey);
  const total = pos + printing + wakala + other;
  return { pos, printing, wakala, other, total };
}

export function weeklyStackedSeries(salesHistory, printJobs, transactions, dayKeys, otherServiceSales = []) {
  return dayKeys.map((dateKey) => ({
    dateKey,
    label: shortWeekdayLabel(dateKey),
    ...businessDayBreakdown(salesHistory, printJobs, transactions, dateKey, otherServiceSales),
  }));
}

export function sumBreakdownOverSeries(rows) {
  return rows.reduce(
    (acc, r) => ({
      pos: acc.pos + r.pos,
      printing: acc.printing + r.printing,
      wakala: acc.wakala + r.wakala,
      other: acc.other + r.other,
      total: acc.total + r.total,
    }),
    { pos: 0, printing: 0, wakala: 0, other: 0, total: 0 }
  );
}
